import { useEffect, useState, useRef } from "react";
import { Image } from "../../AppDataType";
import "./AutoSlider.css";

interface Props {
  images: Image[];
  autoPlay?: boolean;
  transitionDuration?: number;
  interval?: number;
}

const AutoSlider: React.FC<Props> = ({ images, autoPlay = true }) => {
    // El tiempo de la transición debe ser menor al tiempo del intervalo para que no se rompa,
    // asimismo la suma de la transición y del intervalo tiene que dar exacto el total de 
    // la animación de transición definida en el archivo CSS.
    const [transitionDuration, setTransitionDuration] = useState(4000);
    const [slideInterval, setSlideInterval] = useState(8000);

    const [currentIndex, setCurrentIndex] = useState(1);
    const sliderRef = useRef<HTMLDivElement>(null);
    const timerRef = useRef<number | null>(null);
    const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
    const firstSlide = useRef(true);
    const transitionSlideCSS = `transform ${transitionDuration}ms ease-in-out`;
    // Duplicar la última imagen para crear un efecto infinito al volver a la primera diapositiva 
    const extendedImages = [ images[images.length - 1], ...images, ]; 

    /* --- SLIDE INTERVAL & DURATION --- */
    useEffect(() => {
        const updateTimings = () => {
            if (window.innerWidth > 650) {
                setTransitionDuration(4000);
                setSlideInterval(8000);
            }
            else {
                setTransitionDuration(2000);
                setSlideInterval(12000);
            }
        }

        updateTimings();
        window.addEventListener("resize", updateTimings);

        return () => window.removeEventListener("resize", updateTimings);
    }, []);


    /* --- NEXT SLIDE ---- */
    const nextSlide = () => {
        if (!sliderRef.current) return;

        sliderRef.current.style.transition = transitionSlideCSS;
        setCurrentIndex((prev) => prev + 1);
    };

    useEffect(() => {
        if (!sliderRef.current) return;

        // Si es la última imagen, se vuelve al índice 0 (imagen repetida) sin
        // transición para realizar el loop infinito sin transiciones de por medio.
        if (currentIndex === extendedImages.length) {
            sliderRef.current.style.transition = "none";
            setCurrentIndex(0);

            // Esto resuelve el problema de la duración extra cuando se vuelve a
            // la imagen del principio (imagen repetida).
            setTimeout(() => {
                if (!sliderRef.current) return; 
                sliderRef.current.style.transition = transitionSlideCSS;
                setCurrentIndex(1);
            }, 50); // Breve pausa para evitar posibles conflictos visuales
        } 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    /* ---- AUTOPLAY --- */
    useEffect(() => {
        if (!autoPlay) return;

        const manageNextSlide = () => {
            nextSlide();

            // Después del primer temporizador, corregido el tiempo excedente de la 
            // primera imagen, el resto de imagenes se muestran de forma normal
            timerRef.current = setTimeout(manageNextSlide, slideInterval);
        }

        // Este es el primer temporizador que se ejecuta, corrigiendo el tiempo excedente 
        // de la primera imagen (no tiene el tiempo de transición como sí la tiene el resto).
        timerRef.current = setTimeout(manageNextSlide, slideInterval - transitionDuration);

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoPlay, transitionDuration, slideInterval]);

    /* --- RESPONSIVE IMAGE CLASS --- */
    useEffect(() => {
        if (!autoPlay) return;

        imageRefs.current.forEach((img) => {
            img?.classList.remove("responsive-animation");
        })

        if (currentIndex > 0) {
            const currentImg = imageRefs.current[currentIndex];
            if (firstSlide.current) {
                if (!currentImg) return;
                setTimeout(() => {
                    currentImg.classList.add("responsive-animation");
                    firstSlide.current = false;
                }, 50); // Pequeño retraso para evitar problemas visuales
            }
            else {
                if (!currentImg) return;
                setTimeout(() => {
                    currentImg.classList.add("responsive-animation");
                }, transitionDuration);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);


    return (
        <div aria-label="images-slider"
             className="slider-wrapper overflow-hidden h-full relative">
            <div ref={sliderRef} 
                 className="slider-container flex h-full"
                 style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
            {
                extendedImages.map((image, index) => (
                    <div key={index} 
                         className={`slider-item flex-none size-full ${ index === currentIndex && index > 0 ? "slider-active" : ""}`}>
                        <img src={image.src} 
                             alt={image.alt} 
                             ref={(el) => (imageRefs.current[index] = el)}
                             className="size-full object-cover"
                             loading="lazy"/>
                    </div>
                ))
            }
            </div>
        </div>
  );
}

export default AutoSlider;
